import { getIdToken } from '../auth/cognito';
import { notifyAccountBlocked, readIdTokenInMemory } from '../auth/tokens';
import {
  AccountBlockedError,
  NotAuthenticatedError,
  type AssistantReply,
  type CreateNoteRequest,
  type NoteListResponse,
  type NoteResponse,
  type UpdateNoteRequest,
} from './types';

const RAW_BASE = import.meta.env.VITE_API_URL as string;
// Tolerate either trailing-slash or none so paths can always start with '/'.
const API_BASE = RAW_BASE.endsWith('/') ? RAW_BASE.slice(0, -1) : RAW_BASE;

async function authedFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const token = await getIdToken();
  if (!token) throw new NotAuthenticatedError();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
    ...((init.headers as Record<string, string>) ?? {}),
  };

  return fetch(`${API_BASE}${path}`, { ...init, headers });
}

async function throwForStatus(res: Response): Promise<never> {
  if (res.status === 401) throw new NotAuthenticatedError();
  if (res.status === 403) {
    // The backend signals admin-blocked users with a structured body. Anything
    // else (missing scope, generic 403) falls through to a plain Error so the
    // user doesn't get the suspended-account screen for the wrong reason.
    try {
      const data = await res.clone().json();
      if (data && data.error === 'ACCOUNT_BLOCKED') {
        const message = data.message ?? 'Your account has been suspended.';
        notifyAccountBlocked(message);
        throw new AccountBlockedError(message);
      }
    } catch (e) {
      if (e instanceof AccountBlockedError) throw e;
      // not JSON — fall through to the generic error below
    }
  }
  const body = await res.text();
  throw new Error(`${res.status}: ${body}`);
}

export async function call(path: string, init: RequestInit = {}): Promise<AssistantReply> {
  const res = await authedFetch(path, init);
  if (!res.ok) await throwForStatus(res);
  return res.json();
}

// ─── Endpoint shorthands ─────────────────────────────────────────────────────

export function sendMessage(text: string): Promise<AssistantReply> {
  return call('/messages', { method: 'POST', body: JSON.stringify({ text }) });
}

// Tasks + projects — re-added for the Now right rail (focus item, projects
// glance). The Browse surface is still dead; these feed calm rail cards only.

export function listTasks(opts: { status?: string; track?: string } = {}): Promise<AssistantReply> {
  const qs = new URLSearchParams();
  if (opts.status) qs.set('status', opts.status);
  if (opts.track) qs.set('track', opts.track);
  const q = qs.toString();
  return call(`/tasks${q ? `?${q}` : ''}`);
}

export function listProjects(): Promise<AssistantReply> {
  return call('/projects');
}

export function listTasksByPerson(name: string): Promise<AssistantReply> {
  return call(`/tasks/by-person/${encodeURIComponent(name)}`);
}

export function updateTaskStatus(taskSk: string, status: string): Promise<AssistantReply> {
  return call(`/tasks/${encodeURIComponent(taskSk)}`, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  });
}

// ─── M8 Notes ────────────────────────────────────────────────────────────────
// Returns typed Note responses, not AssistantReply — the Notes endpoints
// don't go through the chat-reply shape. `callJson` is a thin generic
// wrapper around the same authedFetch.

async function callJson<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await authedFetch(path, init);
  if (!res.ok) await throwForStatus(res);
  // 204 no-content for delete — caller treats as success
  if (res.status === 204) return undefined as unknown as T;
  return res.json() as Promise<T>;
}

export function listNotes(view?: string): Promise<NoteListResponse> {
  const path = view ? `/notes?view=${encodeURIComponent(view)}` : '/notes';
  return callJson<NoteListResponse>(path);
}

export function getNote(id: string): Promise<NoteResponse> {
  return callJson<NoteResponse>(`/notes/${encodeURIComponent(id)}`);
}

export function createNote(req: CreateNoteRequest): Promise<NoteResponse> {
  return callJson<NoteResponse>('/notes', {
    method: 'POST',
    body: JSON.stringify(req),
  });
}

export function updateNote(id: string, req: UpdateNoteRequest): Promise<NoteResponse> {
  return callJson<NoteResponse>(`/notes/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify(req),
  });
}

export function deleteNote(id: string): Promise<void> {
  return callJson<void>(`/notes/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export function togglePinNote(id: string): Promise<NoteResponse> {
  return callJson<NoteResponse>(`/notes/${encodeURIComponent(id)}/pin`, {
    method: 'POST',
  });
}

export function archiveNote(id: string): Promise<NoteResponse> {
  return callJson<NoteResponse>(`/notes/${encodeURIComponent(id)}/archive`, {
    method: 'POST',
  });
}

export function restoreNote(id: string): Promise<NoteResponse> {
  return callJson<NoteResponse>(`/notes/${encodeURIComponent(id)}/restore`, {
    method: 'POST',
  });
}

export function purgeNote(id: string): Promise<void> {
  return callJson<void>(`/notes/${encodeURIComponent(id)}/purge`, {
    method: 'POST',
  });
}

export function duplicateNote(id: string): Promise<NoteResponse> {
  return callJson<NoteResponse>(`/notes/${encodeURIComponent(id)}/duplicate`, {
    method: 'POST',
  });
}

export function autoTitleNote(id: string): Promise<{ title: string }> {
  return callJson<{ title: string }>(`/notes/${encodeURIComponent(id)}/auto-title`, {
    method: 'POST',
  });
}

/**
 * Fire-and-forget note save for the beforeunload flush. `keepalive: true` lets
 * the request outlive the page teardown (a plain fetch is aborted mid-flight).
 * sendBeacon can't carry the Authorization header, so keepalive fetch it is.
 * Uses the in-memory ID token synchronously — an async refresh can't run
 * during unload; if the token has lapsed the flush is silently skipped (the
 * debounced autosave will already have caught all but the last 1.5s).
 */
export function flushNoteUpdate(id: string, req: UpdateNoteRequest): void {
  const { token } = readIdTokenInMemory();
  if (!token) return;
  void fetch(`${API_BASE}/notes/${encodeURIComponent(id)}`, {
    method: 'PUT',
    keepalive: true,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(req),
  }).catch(() => {
    /* the page is going away — nothing to surface */
  });
}

export function graduateNoteLine(noteId: string, text: string): Promise<{ ok: boolean; fallback: boolean }> {
  return authedFetch(`/notes/${encodeURIComponent(noteId)}/graduate`, {
    method: 'POST',
    body: JSON.stringify({ paragraph: text }),
  }).then((res) => {
    if (res.status === 404) return { ok: false, fallback: true };
    return { ok: res.ok, fallback: false };
  }).catch(() => ({ ok: false, fallback: true }));
}

// ─── M8 — Chat retrieval (RAG) ──────────────────────────────────────────────

export interface Citation {
  // Backend emits "note" | "item" | "event" (per ADR-0009); "task" kept for
  // back-compat with older payloads.
  sourceType: 'note' | 'item' | 'task' | 'event' | string;
  id: string;
  title: string;
  snippet?: string | null;
  distance?: number | null;
}

export interface ChatAskResponse {
  answer: string;
  citations: Citation[];
}

export function askChat(question: string, sourceType?: string): Promise<ChatAskResponse> {
  return callJson<ChatAskResponse>('/chat/ask', {
    method: 'POST',
    body: JSON.stringify({ question, sourceType }),
  });
}

// ─── Recall — thin semantic search (GET /recall?q=) ──────────────────────────
// Wraps the backend's S3-Vectors `search_by_meaning` without the Sonnet
// synthesis loop. Returns matches the caller renders itself (cards/strips).
// Includes dumps/items now that captures are embedded (the wedge hero fix).

export interface RecallMatch {
  sourceType: 'note' | 'item' | 'event' | string;
  id: string;
  title: string;
  snippet?: string | null;
}

export interface RecallResponse {
  query: string;
  matches: RecallMatch[];
}

export function recall(
  query: string,
  opts: { sourceType?: string; limit?: number } = {},
): Promise<RecallResponse> {
  const qs = new URLSearchParams({ q: query });
  if (opts.sourceType) qs.set('source_type', opts.sourceType);
  if (opts.limit) qs.set('limit', String(opts.limit));
  return callJson<RecallResponse>(`/recall?${qs.toString()}`);
}

// ─── Decision log / Nudges ──────────────────────────────────────────────────

export interface DecisionStep {
  label: string;
  value: string;
}

export interface DecisionLogEntry {
  id: string;
  title: string;
  chain: DecisionStep[];
  message: string;
  mode: 'nudge' | 'quiet';
  notifyAt: string | null;
  verdict: 'notify' | 'hold' | null;
  why: string | null;
}

export async function getDecisionLog(): Promise<{ status: 'ok'; entries: DecisionLogEntry[] } | { status: 'unavailable' }> {
  try {
    const res = await authedFetch('/decision-log');
    if (res.status === 404) return { status: 'unavailable' };
    if (!res.ok) await throwForStatus(res);
    const data = await res.json();
    return { status: 'ok', entries: data.entries ?? [] };
  } catch (err) {
    if (err instanceof NotAuthenticatedError) throw err;
    return { status: 'unavailable' };
  }
}

export async function setNotifyMode(itemId: string, mode: 'nudge' | 'quiet'): Promise<void> {
  try {
    const res = await authedFetch(`/items/${encodeURIComponent(itemId)}/notify`, {
      method: 'PATCH',
      body: JSON.stringify({ mode }),
    });
    if (res.status === 401) throw new NotAuthenticatedError();
  } catch (err) {
    if (err instanceof NotAuthenticatedError) throw err;
  }
}

export async function setNudgeTiming(
  itemId: string,
  leadTime: 'same_day' | '1_day' | '3_days' | '1_week',
): Promise<void> {
  try {
    const res = await authedFetch(`/items/${encodeURIComponent(itemId)}/nudge-timing`, {
      method: 'PATCH',
      body: JSON.stringify({ leadTime }),
    });
    if (res.status === 401) throw new NotAuthenticatedError();
  } catch (err) {
    if (err instanceof NotAuthenticatedError) throw err;
  }
}

export async function sendNudgeFeedback(
  itemId: string,
  feedback: 'good' | 'too_early' | 'too_late' | 'never',
): Promise<void> {
  try {
    const res = await authedFetch(`/items/${encodeURIComponent(itemId)}/nudge-feedback`, {
      method: 'POST',
      body: JSON.stringify({ feedback }),
    });
    if (res.status === 401) throw new NotAuthenticatedError();
  } catch (err) {
    if (err instanceof NotAuthenticatedError) throw err;
  }
}

// ─── Preferences ────────────────────────────────────────────────────────────

export interface UserPreferences {
  theme?: 'system' | 'light' | 'dark';
  improveReplies?: boolean;
  meetingBriefings?: boolean;
  actionEvents?: boolean;
  quietMorningBrief?: boolean;
}

export function getPreferences(): Promise<UserPreferences> {
  return callJson<UserPreferences>('/profile/preferences');
}

export function savePreferences(prefs: Partial<UserPreferences>): Promise<UserPreferences> {
  return callJson<UserPreferences>('/profile/preferences', {
    method: 'PUT',
    body: JSON.stringify(prefs),
  });
}

// ─── Calendar connections ──────────────────────────────────────────────────

export interface CalendarConnection {
  provider: 'GOOGLE' | 'APPLE';
  accountEmail: string;
  kind?: string;
  lastSyncedAt?: string | null;
  calendars: { id: string; name: string; colour?: string; enabled: boolean }[];
}

export function listCalendarConnections(): Promise<{ connections: CalendarConnection[] }> {
  return callJson<{ connections: CalendarConnection[] }>('/calendar/connections');
}

export function getCalendarOAuthState(): Promise<{ url: string }> {
  return callJson<{ url: string }>('/calendar/oauth/state');
}

export function toggleSubCalendars(
  email: string,
  toggles: Record<string, boolean>,
): Promise<void> {
  return callJson<void>(`/calendar/connections/${encodeURIComponent(email)}/calendars`, {
    method: 'PATCH',
    body: JSON.stringify({ toggles }),
  });
}

export function triggerCalendarSync(): Promise<void> {
  return callJson<void>('/calendar/sync', { method: 'POST' });
}

// ─── Account ────────────────────────────────────────────────────────────────

export function exportAccount(): Promise<{ url: string }> {
  return callJson<{ url: string }>('/account/export');
}

export function deleteAccount(): Promise<void> {
  return callJson<void>('/account', { method: 'DELETE' });
}

// ─── Capture — POST /photos (OCR → same capture pipeline) ────────────────────
// `imageBase64` is the raw base64 payload (no `data:` prefix). Returns the
// same AssistantReply shape as a typed jot — the OCR'd text is classified and
// routed server-side via Textract → the capture pipeline.

export function sendPhoto(imageBase64: string): Promise<AssistantReply> {
  return call('/photos', {
    method: 'POST',
    body: JSON.stringify({ image: imageBase64 }),
  });
}

// ─── M9 — Calendar ─────────────────────────────────────────────────────────

export interface CalendarEvent {
  provider: 'GOOGLE' | 'APPLE';
  providerId: string;
  title: string;
  location?: string;
  description?: string;
  startAt: string;
  endAt: string;
  allDay: boolean;
  attendees?: string[];
}

export function listCalendarEvents(fromIso: string, toIso: string): Promise<{ events: CalendarEvent[] }> {
  const qs = new URLSearchParams({ from: fromIso, to: toIso }).toString();
  return callJson<{ events: CalendarEvent[] }>(`/calendar/events?${qs}`);
}

export function disconnectCalendar(provider: 'GOOGLE' | 'APPLE', email: string): Promise<void> {
  const qs = new URLSearchParams({ provider, email }).toString();
  return callJson<void>(`/calendar/connect?${qs}`, { method: 'DELETE' });
}
